// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API module for retrieving an accounting close run by ID.
 * Provides type-safe close run retrieval with Zod validation.
 */
import * as z from 'zod';
import { closeRunResponseSchema } from '@/accounting/api/types';
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';

/**
 * Schema for validating the response from the get close run API endpoint.
 * Includes both the close run data and metadata about the request.
 */
const getCloseRunResponseSchema = z
  .object({
    data: closeRunResponseSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, closeRun: data }));

/**
 * Type definition for the get close run API response.
 * Contains the close run data and request metadata.
 */
type GetCloseRunResponse = z.infer<typeof getCloseRunResponseSchema>;

/**
 * Retrieves an accounting close run by its ID.
 *
 * @param id - The unique identifier of the close run to retrieve
 * @returns Promise resolving to the close run data and request metadata
 * @throws Will throw an error if the close run is not found, API request fails, or response validation fails
 */
export async function getCloseRun(id: string): Promise<GetCloseRunResponse> {
  const url = `/v1/accounting/close-runs/${id}`;
  const response = await api.get(url);

  return parseResponse(getCloseRunResponseSchema, response.data, {
    schemaName: 'CloseRun',
    endpoint: url,
  });
}
